import { ResourcesItem, ResourcesList } from 'redux-and-the-rest';
import React, { useEffect, useRef, useState } from 'react';
import { Sprite, Stage, Container } from '@inlet/react-pixi';
import { connect, Provider } from 'react-redux';
import { Point } from 'pixi.js';
import { generatePath, useHistory } from 'react-router-dom';

import Viewport from '../../Viewport';
import SizeSlider from '../rewards/SizeSlider';
import { listStateHandler } from '../../tools/reactUtils/stateUtils';
import { IDraggableReward, DraggableReward } from '../rewards/DraggableReward';
import {
  createPixiDimensions,
  pointCoordinateToPercentage,
  percentageToPointCoordinate,
  stageOptions,
  Scale,
} from './utils';

import IslandSign from './IslandSign/IslandSign';
import { store } from '../../redux/store';
import GameLayer from './GameLayer';
import useModalLink from '../../hooks/useModalLink';
import { ModalRoutes } from '../../routes';
import RewardsExpandableWidget from '../rewards/RewardsExpandableWidgetComponents/RewardsExpandableWidget';
import { ApplicationState } from '../../redux/types';
import {
  deleteRewardPosition, getOrFetchRewardPositions, updateRewardPosition,
} from '../../redux/reducers/rewardPositionReducer';
import { RewardPositionModel } from '../../models/rewardPositionModel';
import { IslandModel } from '../../models/islandModel';
import useWindowSize from '../../hooks/useWindowSize';
import usePreventBodyScroll from '../../hooks/usePreventBodyScroll';
import { getUser } from '../../redux/reducers/userReducer';
import { UserModel } from '../../models/userModel';
import { isRegisteredUser } from '../../utils/UserUtils';
import useElementSize from '../../hooks/useElementSize';

import './Island.scss';

const HIGHLIGHTED_REWARD_ACTION_TYPES = {
  RESIZE: 'RESIZE',
  MOVE: 'MOVE',
};

const SAVE_ISLAND_MESSAGE = 'Save Your Island';

interface Props extends OwnProps {
  rewardPositions: ResourcesList<RewardPositionModel>,
  user: ResourcesItem<UserModel>,
}

interface OwnProps {
  island: IslandModel;
  islandImage: string;
}

const IslandScene = ({
  rewardPositions, island, user, islandImage,
}: Props): React.ReactElement => {
  const history = useHistory();
  usePreventBodyScroll();

  const {
    theme: {
      gamePositions,
    },
    name,
  } = island;

  const { width, height } = useWindowSize();
  const footerDimensions = useElementSize('footer');
  const availableHeight = height - (footerDimensions?.height || 0);
  const registeredUser = isRegisteredUser(user);
  const islandName = !registeredUser ? SAVE_ISLAND_MESSAGE : name as string;
  const islandScale = useRef<Scale>({ x: 1, y: 1 });
  const [selectedRewardIndex, setSelectedRewardIndex] = useState<number | undefined>(undefined);
  const [showRewardsActions, setShowRewardsAction] = useState<boolean>(false);
  const openModal = useModalLink();

  const setIslandScale = (scale: Scale) => {
    islandScale.current = scale;
  };

  const [draggableRewards, setRewards] = listStateHandler(
    useState<IDraggableReward<RewardPositionModel>[]>([]),
  );

  const {
    worldWidth,
    worldHeight,
    worldCentre,
    screenWidth,
    screenHeight,
    islandImageWidth,
    islandImageHeight,
    marginHorizontal,
    marginVertical,
  } = createPixiDimensions(width, availableHeight);

  const [viewportCentre, setViewportCentre] = useState(worldCentre);
  const rewardSize = (worldWidth * 6) / 100;
  const [resizingReward, setRewardResizing] = useState(false);

  /**
   * We must store (in the state) the action we have selected on a reward so that we can
   * rehydrate the DraggableRewards state whenever the rewardPositions have changed.
   *
   * E.g. When we click the move button and move a reward we update one of the rewardPositions.
   * The useEffect is then triggered and we must know what action we were performing on that reward.
   * In this case, it was the MOVE action.
   */
  const [
    highlightedRewardsActionButton, setHighlightedRewardsActionButton,
  ] = useState<string | undefined>(undefined);

  useEffect(() => {
    setRewards.setItems([...rewardPositions.items.map(({ values }, index) => {
      const {
        id,
        percentageOffsetX,
        percentageOffsetY,
        flipped,
        scalePercentage,
        gameScore: { chosenReward: { imageUrl = '', name: rewardName = '' } = {} },
      } = values;

      const draggableReward: IDraggableReward<RewardPositionModel> = {
        id,
        name: rewardName,
        url: imageUrl,
        scale: islandScale,
        flipped,
        scalePercentage,
        sideLength: rewardSize,
        position: new Point(
          percentageToPointCoordinate(percentageOffsetX, worldWidth),
          percentageToPointCoordinate(percentageOffsetY, worldHeight),
        ),
        meta: values,
        /**
         * Make sure the draggable reward props are recalculated after updates
         */
        selected: selectedRewardIndex === index,
        canMove: highlightedRewardsActionButton === HIGHLIGHTED_REWARD_ACTION_TYPES.MOVE,
        canResize: selectedRewardIndex === index && resizingReward,
      };

      return draggableReward;
    })]);
  }, [rewardPositions, worldWidth, worldHeight]);

  const onRewardDelete = () => {
    if (selectedRewardIndex !== undefined) {
      deleteRewardPosition({
        islandId: island.id,
        id: draggableRewards[selectedRewardIndex].meta.id,
      });

      setSelectedRewardIndex(undefined);
      setShowRewardsAction(false);
      setRewardResizing(false);
      setHighlightedRewardsActionButton(undefined);
    }
  };

  const [clickedAway, setClickedAway] = useState(false);

  useEffect(() => {
    if (!clickedAway || selectedRewardIndex === undefined) {
      return;
    }

    const rewardIndex = draggableRewards
      .findIndex((item) => item.selected);

    if (rewardIndex === -1) {
      return;
    }

    const isRewardSelected = draggableRewards[rewardIndex].selected;

    if (!isRewardSelected) {
      return;
    }

    setRewards.updateAtIndex(
      rewardIndex,
      (slicedReward) => ({ ...slicedReward, selected: !isRewardSelected, canResize: false }),
      {
        /**
         * Disable actions on the other rewards.
         */
        updateOthers: (slicedReward) => ({
          ...slicedReward,
          selected: false,
          canMove: false,
        }),
      },
    );

    setClickedAway(false);
    setSelectedRewardIndex(undefined);
    setShowRewardsAction(false);
    setRewardResizing(false);
    setHighlightedRewardsActionButton(undefined);
  }, [clickedAway]);

  const handleStageClick = () => {
    setClickedAway(true);
  };

  const onRewardMove = () => {
    if (selectedRewardIndex !== undefined) {
      setRewards.updateAtIndex(
        selectedRewardIndex,
        (slicedReward) => {
          if (!slicedReward.canMove) {
            setHighlightedRewardsActionButton(HIGHLIGHTED_REWARD_ACTION_TYPES.MOVE);
          } else {
            setHighlightedRewardsActionButton(undefined);
          }

          /**
           * Turn off resizing when the move button is pressed.
           */
          setRewardResizing(false);

          return { ...slicedReward, canMove: !slicedReward.canMove, canResize: false };
        },
      );
    }
  };

  const onRewardResize = () => {
    if (!resizingReward) {
      setHighlightedRewardsActionButton(HIGHLIGHTED_REWARD_ACTION_TYPES.RESIZE);
    } else {
      setHighlightedRewardsActionButton(undefined);
    }

    /**
     * Turn off moving the rewards when the resizing button has been pressed.
     */
    if (selectedRewardIndex !== undefined) {
      setRewards.updateAtIndex(
        selectedRewardIndex,
        (slicedReward) => ({ ...slicedReward, canMove: false, canResize: !slicedReward.canResize }),
      );
    }

    setRewardResizing(!resizingReward);
  };

  const manageIslandsUrl = isRegisteredUser(user) ? generatePath(ModalRoutes.Island.ManageIsland, {
    id: island.id,
  }) : generatePath(ModalRoutes.Island.RegistrationPrompt, {
    id: island.id,
  });

  /**
   * Sorting the image to fix a bug with <Sprite />.zIndex wherein
   * setting the zIndex to a larger number based on the currently
   * `selected` (`draggableReward`[].`selected`) doesn't work as a
   * result of a bug in Pixi.js
   *
   * The fix stems from the fact that the first <Sprite /> in a
   * Pixi <Container /> always has the highest zIndex, so we move
   * the `selected` one to the front of the array, and ensure
   * <DraggableRewards /> isn't wrapped by a <Container /> internally,
   * and instead all <Sprite /> `draggableReward` (re: <DraggableRewards />)
   * renders are instead wrapped by a <Container />.
   */
  const draggableRewardsSorted = JSON.parse(JSON.stringify(draggableRewards))
    .sort((a: IDraggableReward<RewardPositionModel>, b: IDraggableReward<RewardPositionModel>) => {
      if (a.selected === b.selected) {
        return 0;
      } if (a.selected) {
        return 1;
      }
      return -1;
    });

  return (
    <div className="island island-fade">
      {resizingReward && selectedRewardIndex !== undefined && (
        <div style={{ position: 'absolute', left: 0, top: '25%' }}>
          <SizeSlider
            sliderHeight={`${screenHeight / 2}px`}
            onSliderRelease={(value) => {
              if (selectedRewardIndex !== undefined) {
                const rewardPosition = draggableRewards[selectedRewardIndex].meta;

                updateRewardPosition({
                  islandId: island.id,
                  id: rewardPosition.id,
                  include: 'gameScore.chosenReward',
                }, {
                  ...rewardPosition,
                  scalePercentage: value,
                });
              }
            }}
            onChange={(value) => {
              if (selectedRewardIndex !== undefined) {
                setRewards.updateAtIndex(
                  selectedRewardIndex,
                  (slicedReward) => ({ ...slicedReward, scalePercentage: value }),
                );
              }
            }}
            percentage={draggableRewards[selectedRewardIndex].scalePercentage}
          />
        </div>
      )}

      <RewardsExpandableWidget placementOfNewRewards={{
        percentageOffsetX: pointCoordinateToPercentage(viewportCentre.x, worldWidth),
        percentageOffsetY: pointCoordinateToPercentage(viewportCentre.y, worldHeight),
      }}
      />

      {
       /**
        * The router does not work within the canvas scene generated by the stage. Any router functions
        * that you need such as the history.push mush be passed as a prop to the components on the stage.
        */
      }
      <Stage
        width={screenWidth}
        height={screenHeight}
        options={stageOptions}
      >
        <Provider store={store}>
          <Viewport
            screenWidth={screenWidth}
            screenHeight={screenHeight}
            worldWidth={worldWidth}
            worldHeight={worldHeight}
            onZoom={(x, y) => setIslandScale({ x, y })}
            onMoveEnd={(centre) => setViewportCentre(centre)}
            onClick={handleStageClick}
          >
            {islandImage && (
              <Sprite
                image={islandImage}
                width={islandImageWidth}
                height={islandImageHeight}
                anchor={{ x: marginHorizontal, y: marginVertical }}
              />
            )}

            <IslandSign
              islandName={islandName}
              worldWidth={worldWidth}
              worldHeight={worldHeight}
              onSaveIsland={() => openModal(manageIslandsUrl)}
            />

            <GameLayer
              worldWidth={worldWidth}
              worldHeight={worldHeight}
              gamePositions={gamePositions}
              onHistoryPush={(route) => history.push(route)}
              islandId={island.id}
            />

            <Container>
              {
                draggableRewardsSorted.map((item: IDraggableReward<RewardPositionModel>) => (
                  <DraggableReward
                    key={item.id}
                    item={item}
                    onMove={onRewardMove}
                    onResize={onRewardResize}
                    onDelete={onRewardDelete}
                    worldCentre={worldCentre}
                    worldHeight={worldHeight}
                    onFlip={() => {
                      if (selectedRewardIndex !== undefined) {
                        const rewardPosition = draggableRewards[selectedRewardIndex].meta;

                        updateRewardPosition({
                          islandId: island.id,
                          id: rewardPosition.id,
                          include: 'gameScore.chosenReward',
                        }, {
                          ...rewardPosition,
                          flipped: !rewardPosition.flipped,
                        });
                      }
                    }}
                    onFinishingMoving={(point) => {
                      if (selectedRewardIndex !== undefined) {
                        const rewardPosition = draggableRewards[selectedRewardIndex].meta;

                        updateRewardPosition({
                          islandId: island.id,
                          id: rewardPosition.id,
                          include: 'gameScore.chosenReward',
                        }, {
                          ...rewardPosition,
                          percentageOffsetX: pointCoordinateToPercentage(point.x, worldWidth),
                          percentageOffsetY: pointCoordinateToPercentage(point.y, worldHeight),
                        });
                      }
                    }}
                    onRewardClicked={() => {
                      const rewardIndex = draggableRewards
                        .findIndex(({ id: displayRewardId }) => item.id === displayRewardId);

                      if (rewardIndex !== -1) {
                        const isRewardSelected = draggableRewards[rewardIndex].selected;

                        setRewards.updateAtIndex(
                          rewardIndex,
                          (slicedReward) => ({ ...slicedReward, selected: !isRewardSelected }),
                          {
                            /**
                               * Disable actions on the other rewards.
                               */
                            updateOthers: (slicedReward) => ({
                              ...slicedReward,
                              selected: false,
                              canMove: false,
                              canResize: false,
                            }),
                          },
                        );

                        setSelectedRewardIndex(() => (isRewardSelected ? undefined : rewardIndex));
                        setShowRewardsAction(!showRewardsActions);
                        setRewardResizing(false);
                        setHighlightedRewardsActionButton(undefined);
                      }
                    }}
                  />
                ))
              }
            </Container>
          </Viewport>
        </Provider>
      </Stage>
    </div>
  );
};

const mapStateToProps = (
  { rewardPositions, users }: ApplicationState,
  { island: { id: islandId } }: OwnProps,
) => ({
  rewardPositions: getOrFetchRewardPositions(rewardPositions, {
    islandId, include: 'gameScore.chosenReward',
  }),
  user: getUser(users),
});

export default connect(mapStateToProps)(IslandScene);
